import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCoresRecentes, registrarCorUsada } from "../hooks/useCoresRecentes";
import { UsuarioAtivo } from "../services/usuarios";
import { COR_HEX, COR_GRIFO, RE_HEX, boxChecklistHtml, htmlParaMarkup, markupParaHtml, rgbParaHex } from "../utils/markupRico";

interface MentionTextareaProps {
  value: string;
  onChange: (v: string) => void;
  usuarios: UsuarioAtivo[];
  placeholder?: string;
  rows?: number;
  className?: string;
  /** Se passado, colar imagem faz upload e insere a imagem no cursor (inline). */
  onUploadImagem?: (file: File) => Promise<string>;
  /** Mostra a barra de formatação (negrito/itálico/grifar/cor). */
  toolbar?: boolean;
  /**
   * Se passado, ENTER (sem Shift) chama isto em vez de quebrar linha — pra usar
   * o editor como campo de "enviar com Enter" (ex.: adicionar anotação). Shift+
   * Enter continua fazendo nova linha. Quando o dropdown de @menção está aberto,
   * o Enter é do dropdown (seleciona a pessoa), não daqui.
   */
  onEnter?: () => void;
}

/**
 * Campo de comentário/descrição COM FORMATAÇÃO VISÍVEL (jul/2026).
 *
 * Antes era um `<textarea>` e a formatação aparecia como código: quem escrevia
 * via `{azul}**texto**{/azul}` na tela. O Ricardo relatou que isso confundia a
 * equipe. Agora é um `contenteditable`: a palavra já aparece azul e em negrito.
 *
 * O TEXTO SALVO NÃO MUDOU — continua o mesmo markdown leve de sempre, traduzido
 * nos dois sentidos por `utils/markupRico`. Isso é o que mantém compatível: os
 * comentários antigos abrem normalmente e o portal do cliente segue lendo igual.
 *
 * O nome do arquivo/componente foi mantido de propósito: ele é usado em
 * `NovaTarefaModal` e `TarefaDrawer`, e a interface (props) é idêntica à antiga.
 */

// Tamanho da barra flutuante e do submenu de cores. Fixos porque a posição é
// calculada ANTES de eles existirem na tela (não dá pra medir o que ainda não
// foi renderizado) — e o conteúdo é fixo, então medir não traria ganho.
const LARGURA_BOLHA = 254;
const ALTURA_BOLHA = 36;
const LARGURA_PALETA = 224;
const ALTURA_PALETA = 250;

function handleFromNome(nome: string | null | undefined) {
  // Tolera nome ausente (null/undefined/""): sem isso, um único usuário com nome
  // faltando derrubava TODA a tela ao digitar "@" + letra (o filtro roda sobre a
  // lista inteira e a exceção não tinha ErrorBoundary pra segurar). Ver
  // MENTION-mencao / episódio jul/2026 (tela branca no briefing).
  return (nome || "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, ".");
}

const RE_MENCAO = /@([\wÀ-ÿ.\-]*)$/;

/**
 * Cor "impossível" usada só pra MARCAR o que precisa ser limpo.
 *
 * `execCommand` sabe APLICAR cor, mas não sabe tirar. Então, pra desligar, a
 * gente pinta a seleção com este sentinela e logo em seguida varre o editor
 * removendo a propriedade de quem ficou com ele — o span sem estilo nenhum é
 * desembrulhado. Resultado: o texto volta a herdar a cor do tema (que é o que
 * "escrever normalmente" significa), em vez de virar um preto fixo que ficaria
 * invisível no modo escuro.
 */
const SENTINELA = "#010203";

/**
 * Paleta extra do arco-íris (a barra fixa já tem as 5 principais).
 *
 * PRETO PURO FICOU DE FORA de propósito: no modo escuro ele some no fundo, e
 * era o que a pessoa clicava achando que "voltava ao normal" — o texto sumia.
 * Pra voltar ao normal existe o botão "Padrão", que TIRA a cor e deixa o texto
 * herdar a do tema (branco no escuro, escuro no claro).
 */
const PALETA_EXTRA = [
  "#334155", "#64748b", "#94a3b8", "#f43f5e", "#ec4899",
  "#a855f7", "#6366f1", "#0ea5e9", "#06b6d4", "#14b8a6",
  "#22c55e", "#84cc16", "#eab308", "#f59e0b", "#78350f",
];

type EstadoFormato = { negrito: boolean; italico: boolean; grifo: boolean; cor: string };

export function MentionTextarea({
  value,
  onChange,
  usuarios,
  placeholder,
  rows = 3,
  className,
  onUploadImagem,
  toolbar,
  onEnter,
}: MentionTextareaProps) {
  const edRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [enviandoImg, setEnviandoImg] = useState(false);
  // Destaque enquanto a pessoa arrasta uma imagem por cima da caixa.
  const [dragImg, setDragImg] = useState(false);
  const [vazio, setVazio] = useState(!value);
  // O que está LIGADO na posição do cursor — alimenta o destaque da barra.
  const [fmt, setFmt] = useState<EstadoFormato>({ negrito: false, italico: false, grifo: false, cor: "" });
  // O submenu de cores é ancorado no BOTÃO que o abriu (barra de cima ou barra
  // flutuante) e vai em portal, com posição fixa — igual à barra flutuante, pelo
  // mesmo motivo: o editor mora em painéis com rolagem que recortariam um
  // `absolute`. Antes ele era absoluto no topo do editor e por isso não dava pra
  // oferecer "mais cores" na barra flutuante.
  const [paletaAberta, setPaletaAberta] = useState(false);
  const [paletaAncora, setPaletaAncora] = useState<DOMRect | null>(null);
  const paletaRef = useRef<HTMLDivElement>(null);
  function abrirPaleta(botao: HTMLElement) {
    setPaletaAncora(botao.getBoundingClientRect());
    setPaletaAberta((v) => !v);
  }
  // Fecha ao clicar fora e ao rolar (a âncora é um retângulo congelado — rolar
  // com ele aberto deixaria o submenu boiando longe do botão). Popover PODE
  // fechar assim; a regra de "só fecha no X" vale pra formulário, onde se perde
  // texto digitado.
  useEffect(() => {
    if (!paletaAberta) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (paletaRef.current?.contains(alvo)) return;
      if ((alvo as HTMLElement)?.closest?.("[title='Mais cores']")) return; // o próprio botão alterna
      setPaletaAberta(false);
    };
    const rolou = () => setPaletaAberta(false);
    document.addEventListener("mousedown", fora);
    window.addEventListener("scroll", rolou, true);
    return () => {
      document.removeEventListener("mousedown", fora);
      window.removeEventListener("scroll", rolou, true);
    };
  }, [paletaAberta]);
  // Formatação escolhida com o cursor parado (ainda não existe no DOM).
  const pendente = useRef<{ marcas: Partial<EstadoFormato>; ancora: Node | null; offset: number } | null>(null);

  // Último markup que ESTE componente emitiu. Serve pra distinguir "o pai mandou
  // um valor novo" de "é o eco do que acabei de digitar". Sem isso, reescrever o
  // innerHTML a cada tecla jogaria o cursor pro começo a cada letra.
  const ultimoEmitido = useRef<string>("");

  useEffect(() => {
    if (!edRef.current) return;
    if (value === ultimoEmitido.current) return;
    edRef.current.innerHTML = markupParaHtml(value || "");
    ultimoEmitido.current = value;
    setVazio(!value);
  }, [value]);

  /**
   * Remove qualquer resquício do sentinela do editor.
   *
   * CRÍTICO — o bug que isso conserta: com o cursor parado (sem seleção), o
   * `execCommand` do sentinela não muda o DOM, só deixa "o próximo texto sai
   * assim" pendente no navegador. A varredura da hora não achava nada, e aí a
   * pessoa digitava e o texto novo nascia com `rgb(1,2,3)` — quase preto,
   * ilegível no modo escuro. Era o "a cor padrão volta escura" relatado pelo
   * Ricardo. Rodando isto a cada emissão, o sentinela é apagado assim que
   * aparece e o texto volta a HERDAR a cor do tema.
   */
  function varrerSentinelas() {
    const ed = edRef.current;
    if (!ed) return;
    ed.querySelectorAll<HTMLElement>("span,font,mark").forEach((el) => {
      let mudou = false;
      if (rgbParaHex(el.style.color || "") === SENTINELA) {
        el.style.removeProperty("color");
        mudou = true;
      }
      if (rgbParaHex(el.style.backgroundColor || "") === SENTINELA) {
        el.style.removeProperty("background-color");
        mudou = true;
      }
      const attr = el.getAttribute("color");
      if (attr && rgbParaHex(attr) === SENTINELA) {
        el.removeAttribute("color");
        mudou = true;
      }
      // Span que ficou sem estilo nenhum não serve pra nada: desembrulha. O nó
      // de texto sobrevive, então o cursor de quem está digitando não se perde.
      if (mudou && !el.getAttribute("style") && el.tagName !== "MARK") desembrulhar(el);
    });
  }

  /** Lê o editor, converte pro texto salvo e avisa o pai. */
  function emitir() {
    if (!edRef.current) return;
    varrerSentinelas();
    const md = htmlParaMarkup(edRef.current);
    ultimoEmitido.current = md;
    setVazio(md.trim().length === 0);
    onChange(md);
  }

  /**
   * Lê o que está ativo na posição do cursor. Roda no `selectionchange`, então
   * a barra acompanha o cursor mesmo quando a pessoa só clica no meio do texto.
   *
   * COR e GRIFO são lidos SUBINDO a árvore a partir do cursor e olhando só o
   * `style` INLINE dos elementos dentro do editor. Não use
   * `queryCommandValue("backColor")`: ele devolve o fundo da PÁGINA
   * (`rgb(255,255,255)`) mesmo sem grifo nenhum, e o botão "G" ficava aceso o
   * tempo todo — bug pego no teste, jul/2026. `foreColor` tem o mesmo vício:
   * devolve a cor herdada do tema quando não há cor aplicada.
   *
   * Negrito/itálico seguem no `queryCommandState`, que é confiável e ainda
   * acerta o caso de "vou começar a digitar em negrito" (sem seleção).
   */
  function lerFormato() {
    const ed = edRef.current;
    if (!ed) return;
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || !ed.contains(sel.anchorNode)) return;

    let cor = "";
    let grifo = false;
    let no: Node | null = sel.anchorNode;
    while (no && no !== ed) {
      if (no.nodeType === Node.ELEMENT_NODE) {
        const el = no as HTMLElement;
        if (!cor) cor = rgbParaHex(el.style.color || "");
        if (!grifo) grifo = el.tagName === "MARK" || !!rgbParaHex(el.style.backgroundColor || "");
      }
      no = no.parentNode;
    }

    let negrito = false;
    let italico = false;
    try {
      negrito = document.queryCommandState("bold");
      italico = document.queryCommandState("italic");
    } catch {
      /* navegador sem suporte: barra fica sem destaque, mas escrever funciona */
    }

    // INTENÇÃO PENDENTE: clicar numa cor sem nada selecionado não muda o DOM —
    // o navegador só guarda "o próximo texto sai assim". Sem isso a bolinha só
    // acendia depois de digitar a primeira letra (relatado pelo Ricardo). A
    // intenção vale enquanto o cursor não sair do lugar; ao digitar, o offset
    // muda, ela é descartada e a leitura do DOM assume (já correta).
    const p = pendente.current;
    const mesmaPos = !!p && p.ancora === sel.anchorNode && p.offset === sel.anchorOffset;
    if (!mesmaPos) pendente.current = null;
    setFmt(mesmaPos ? { negrito, italico, grifo, cor, ...p!.marcas } : { negrito, italico, grifo, cor });
  }

  /** Registra "o próximo texto sai assim" e acende o botão na hora. */
  function marcarPendente(parcial: Partial<EstadoFormato>) {
    const sel = window.getSelection();
    pendente.current = {
      marcas: { ...(pendente.current?.marcas || {}), ...parcial },
      ancora: sel?.anchorNode ?? null,
      offset: sel?.anchorOffset ?? 0,
    };
    setFmt((f) => ({ ...f, ...parcial }));
  }

  /**
   * BARRA FLUTUANTE (estilo Word/Notion). Pedido do Ricardo (ago/2026): com
   * muitas imagens o campo fica alto, e pra grifar um texto lá embaixo ele
   * precisava rolar até a barra de cima, clicar, e rolar de volta pra conferir.
   * Agora, ao SELECIONAR um trecho, os botões aparecem coladinhos na seleção.
   *
   * Guarda o RETÂNGULO da seleção (posição fixa na janela) — a barra é
   * renderizada em portal no body porque o editor mora dentro de painéis com
   * rolagem (drawer, modal, coluna do kanban) e um `absolute` seria recortado.
   */
  const [bolha, setBolha] = useState<{ left: number; top: number; bottom: number } | null>(null);

  function recalcularBolha() {
    const ed = edRef.current;
    const sel = window.getSelection();
    if (!toolbar || !ed || !sel || sel.rangeCount === 0 || sel.isCollapsed) { setBolha(null); return; }
    const range = sel.getRangeAt(0);
    // Só quando a seleção é DESTE editor (a página pode ter vários).
    if (!ed.contains(range.commonAncestorContainer)) { setBolha(null); return; }
    const r = range.getBoundingClientRect();
    // Seleção sem área (ex.: só um <br>) não rende barra.
    if (r.width === 0 && r.height === 0) { setBolha(null); return; }
    setBolha({ left: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  }

  useEffect(() => {
    const h = () => { lerFormato(); recalcularBolha(); };
    document.addEventListener("selectionchange", h);
    // Rolar/redimensionar move a seleção na tela — a barra tem que acompanhar.
    // `capture` pra pegar a rolagem dos painéis internos, não só a da janela.
    const mover = () => recalcularBolha();
    window.addEventListener("scroll", mover, true);
    window.addEventListener("resize", mover);
    return () => {
      document.removeEventListener("selectionchange", h);
      window.removeEventListener("scroll", mover, true);
      window.removeEventListener("resize", mover);
    };
  }, [toolbar]);

  /** Aplica formatação na seleção. `styleWithCSS` força <span style> (previsível). */
  function comando(cmd: string, valor?: string) {
    setOpen(false);
    edRef.current?.focus();
    try {
      document.execCommand("styleWithCSS", false, "true");
      document.execCommand(cmd, false, valor);
    } catch {
      /* navegador sem suporte: a formatação simplesmente não aplica */
    }
    emitir();
    lerFormato();
  }

  /** Lista com marcadores / numerada (2ª leva). execCommand cria <ul>/<ol>. */
  function comandoLista(cmd: "insertUnorderedList" | "insertOrderedList") {
    setOpen(false);
    edRef.current?.focus();
    try { document.execCommand(cmd); } catch { /* sem suporte */ }
    emitir();
    lerFormato();
  }

  /**
   * Blocos (linhas) do editor tocados pela seleção atual. Linha "solta" na raiz
   * do contenteditable (o 1º parágrafo costuma vir sem <div>) é embrulhada num
   * <div> antes, pra todo mundo virar bloco e o resto do código ficar simples.
   */
  function blocosDaSelecao(ancora?: HTMLElement | null): HTMLElement[] {
    const ed = edRef.current;
    const sel = window.getSelection();
    if (!ed || !sel || sel.rangeCount === 0) return [];
    const range = sel.getRangeAt(0);
    const subirAteBloco = (n: Node | null): HTMLElement | null => {
      while (n && n.parentNode !== ed) n = n.parentNode;
      return (n as HTMLElement) || null;
    };

    // Embrulha nós soltos da raiz (texto/span sem <div> em volta).
    const soltos = Array.from(ed.childNodes).filter(
      (n) => n.nodeType === Node.TEXT_NODE || (n as HTMLElement).tagName === "SPAN" || (n as HTMLElement).tagName === "BR",
    );
    if (soltos.length) {
      const div = document.createElement("div");
      soltos.forEach((n) => div.appendChild(n));
      ed.insertBefore(div, ed.firstChild);
    }

    const blocos = Array.from(ed.children) as HTMLElement[];
    const tocados = blocos.filter((b) => range.intersectsNode(b));
    if (tocados.length) return tocados;
    // O embrulho acima TIRA os nós soltos do documento por um instante, e o
    // navegador descarta a seleção quando isso acontece — aí o `range` acima
    // vira lixo e nada casa. Por isso o nó-marca vem primeiro: ele está no DOM
    // e sobrevive à remontagem. (Sem ele, clicar no ☑ na PRIMEIRA linha digitada
    // num campo vazio caía no caminho "sem bloco" e bagunçava o texto.)
    const porAncora = ancora ? subirAteBloco(ancora) : null;
    if (porAncora) return [porAncora];
    // Sem interseção (cursor colapsado): acha o bloco do cursor subindo a árvore.
    const n = subirAteBloco(range.startContainer);
    return n ? [n] : [];
  }

  const temCaixa = (b: HTMLElement) => !!b.querySelector(":scope > .rt-box");
  /**
   * Texto do bloco IGNORANDO a caixa do checklist. O glifo ☐/☑ mora dentro do
   * bloco, então `textContent` puro nunca dá vazio num item de checklist — e era
   * por isso que o Enter em item vazio não encerrava a lista.
   */
  function textoSemCaixa(b: HTMLElement): string {
    const box = b.querySelector(":scope > .rt-box");
    return Array.from(b.childNodes)
      .filter((n) => n !== box)
      .map((n) => n.textContent || "")
      .join("")
      .replace(/​/g, "")
      .trim();
  }
  const soCaixa = (b: HTMLElement) => textoSemCaixa(b) === "";

  /**
   * Krava um <span> vazio na posição do cursor e devolve ele.
   *
   * CRÍTICO: `blocosDaSelecao()` REMONTA a raiz do editor (embrulha os nós
   * soltos num <div>), e o navegador descarta a seleção quando o nó dela sai do
   * documento — mesmo que volte logo depois. Um Range lido antes vira lixo e um
   * lido depois aponta pro começo do editor. Com um nó-marca no DOM, a posição
   * sobrevive a qualquer remontagem. Devolve null se o cursor não está aqui
   * dentro ou se há texto selecionado.
   */
  function marcarCursor(): HTMLElement | null {
    const ed = edRef.current;
    const sel = window.getSelection();
    if (!ed || !sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!range.collapsed || !ed.contains(range.startContainer)) return null;
    const marca = document.createElement("span");
    marca.setAttribute("data-ck-cursor", "1");
    range.insertNode(marca);
    return marca;
  }

  /** A marca do cursor está no COMEÇO da linha? (ignora o zero-width da caixa) */
  function marcaNoInicio(bloco: HTMLElement, marca: HTMLElement): boolean {
    const antes = document.createRange();
    antes.selectNodeContents(bloco);
    antes.setEndBefore(marca);
    return antes.toString().replace(/​/g, "").trim() === "";
  }

  /**
   * Quebra a linha NA MARCA do cursor e começa um item de checklist ali: o que
   * estava antes fica na linha atual, o que estava depois vai pro item novo.
   * Pedido da Thainá (ago/2026): "colocar a caixinha sempre onde o cursor
   * estiver" — antes a caixa pulava pro começo da linha, então escrever
   * "Segue os ajustes:" e clicar no ☑ virava "☐ Segue os ajustes:".
   */
  function caixaNoCursor(bloco: HTMLElement, marca: HTMLElement) {
    const fim = document.createRange();
    fim.selectNodeContents(bloco);
    fim.setStartAfter(marca);
    const resto = fim.extractContents();   // o que vinha depois do cursor
    marca.remove();

    const novo = document.createElement("div");
    novo.innerHTML = `${boxChecklistHtml(false)}​`;
    novo.appendChild(resto);
    bloco.after(novo);
    // Linha de origem vazia colapsaria na tela — <br> segura a altura dela.
    if (!bloco.textContent?.replace(/​/g, "").trim()) bloco.innerHTML = "<br>";

    // Cursor logo DEPOIS da caixa (no zero-width), pronto pra digitar o item.
    const sel = window.getSelection();
    const zw = novo.childNodes[1];
    if (sel && zw) {
      const r = document.createRange();
      r.setStart(zw, (zw.textContent || "").length);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  }

  /**
   * CHECKLIST. Antes só inseria UMA caixa vazia no cursor — pra fazer 6 itens a
   * pessoa clicava 6 vezes, e não dava pra transformar uma lista já escrita
   * (reclamação da Thainá, ago/2026: ela escreveu os ajustes como lista numerada
   * e queria cada item marcável). Hoje:
   *   - com texto SELECIONADO: CONVERTE cada linha em item — e clicar de novo
   *     desmarca (tira as caixas);
   *   - com o cursor no MEIO/FIM de uma linha escrita: quebra ali e começa um
   *     item novo NO CURSOR (ver `caixaNoCursor`);
   *   - com o cursor no COMEÇO da linha: converte a linha inteira (é o que se
   *     espera ao "marcar esta linha");
   *   - em linha vazia: insere uma caixa nova, como antes.
   */
  function inserirChecklist() {
    setOpen(false);
    edRef.current?.focus();
    // A marca entra ANTES de qualquer mexida no DOM (ver `marcarCursor`).
    const marca = marcarCursor();
    const blocos = blocosDaSelecao(marca).filter((b) => !soCaixa(b) || temCaixa(b));

    if (blocos.length === 0) {
      marca?.remove();
      // Linha vazia / sem bloco: comportamento antigo (cria o 1º item).
      try {
        document.execCommand("insertHTML", false, `<div>${boxChecklistHtml(false)}​</div>`);
      } catch { /* ignora */ }
      emitir();
      return;
    }

    // Se TODAS já são checklist, o botão desliga (tira as caixas).
    const desligar = blocos.every(temCaixa);

    // Cursor parado (sem seleção) numa única linha que já tem texto e ainda não
    // é checklist: a caixa nasce NO CURSOR em vez de pular pro início da linha.
    if (
      marca && !desligar && blocos.length === 1 &&
      !temCaixa(blocos[0]) && blocos[0].contains(marca) && !marcaNoInicio(blocos[0], marca)
    ) {
      caixaNoCursor(blocos[0], marca);
      emitir();
      return;
    }
    // Fora desse caso a marca não serve pra nada — sai antes de virar markup.
    marca?.remove();
    for (const b of blocos) {
      const caixa = b.querySelector(":scope > .rt-box");
      if (desligar) {
        caixa?.remove();
        // some com o zero-width que ficava depois da caixa
        b.innerHTML = b.innerHTML.replace(/^​/, "");
      } else if (!caixa) {
        // Tira o marcador de lista/numeração pra não ficar "1. ☐ item".
        b.innerHTML = b.innerHTML.replace(/^\s*(\d+[.)]|[-•*])\s+/, "");
        b.insertAdjacentHTML("afterbegin", `${boxChecklistHtml(false)}​`);
      }
    }
    emitir();
  }

  /**
   * ENTER dentro de um item de checklist CONTINUA a lista (cria o próximo item),
   * como no Google Docs/Notion. Enter num item VAZIO encerra a lista (vira linha
   * normal) — senão não teria como sair dela.
   * Devolve true quando tratou o Enter (o chamador deve dar preventDefault).
   */
  function enterNoChecklist(): boolean {
    const ed = edRef.current;
    const sel = window.getSelection();
    if (!ed || !sel || sel.rangeCount === 0) return false;
    let n: Node | null = sel.getRangeAt(0).startContainer;
    while (n && n.parentNode !== ed) n = n.parentNode;
    const bloco = n as HTMLElement | null;
    if (!bloco || !bloco.querySelector?.(":scope > .rt-box")) return false;

    const vazio = textoSemCaixa(bloco) === "";
    if (vazio) {
      // Item vazio → sai da lista: tira a caixa e deixa a linha limpa.
      bloco.innerHTML = "<br>";
      const r = document.createRange();
      r.setStart(bloco, 0); r.collapse(true);
      sel.removeAllRanges(); sel.addRange(r);
      emitir();
      return true;
    }
    const novo = document.createElement("div");
    novo.innerHTML = `${boxChecklistHtml(false)}​`;
    bloco.after(novo);
    const r = document.createRange();
    r.setStart(novo, novo.childNodes.length); r.collapse(true);
    sel.removeAllRanges(); sel.addRange(r);
    emitir();
    return true;
  }

  /** Clique na caixa .rt-box → alterna marcado/desmarcado e salva. */
  function aoClicar(e: React.MouseEvent<HTMLDivElement>) {
    const alvo = e.target as HTMLElement;
    if (!alvo?.classList?.contains("rt-box")) { lerFormato(); return; }
    e.preventDefault();
    const marcado = alvo.getAttribute("data-checked") === "1";
    alvo.setAttribute("data-checked", marcado ? "0" : "1");
    alvo.textContent = marcado ? "☐" : "☑";
    emitir();
  }

  /**
   * Tamanho de fonte (2ª leva): envolve a seleção num <span style="font-size">.
   * execCommand("fontSize") só faz a escala 1-7 (grosseira e com <font>), então
   * fazemos na mão em `em`. Precisa de SELEÇÃO (sem seleção não há o que crescer).
   */
  function aplicarFonte(nivel: "grande" | "pequeno") {
    const ed = edRef.current;
    if (!ed) return;
    setOpen(false);
    ed.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    // Sem seleção: pega a PALAVRA do cursor, pra "clicar e já ficar grande"
    // funcionar sem precisar selecionar antes (pedido do Ricardo).
    if (sel.isCollapsed) {
      try {
        (sel as any).modify("move", "backward", "word");
        (sel as any).modify("extend", "forward", "word");
      } catch {
        /* navegador sem sel.modify: aí precisa selecionar mesmo */
      }
      if (sel.isCollapsed) return;
    }
    const span = document.createElement("span");
    span.style.fontSize = nivel === "grande" ? "1.3em" : "0.82em";
    try {
      const range = sel.getRangeAt(0);
      try {
        range.surroundContents(span);
      } catch {
        // seleção cruza fronteiras de elemento: extrai e reembrulha
        span.appendChild(range.extractContents());
        range.insertNode(span);
      }
      const r2 = document.createRange();
      r2.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(r2);
    } catch {
      /* ignora */
    }
    emitir();
  }

  /** Tira `<span>` da árvore mantendo o conteúdo (usado ao desligar cor/grifo). */
  function desembrulhar(el: HTMLElement) {
    const pai = el.parentNode;
    if (!pai) return;
    while (el.firstChild) pai.insertBefore(el.firstChild, el);
    pai.removeChild(el);
  }

  /**
   * Desliga cor ou grifo: pinta com o sentinela e limpa quem ficou com ele.
   * Ver comentário do `SENTINELA` pra saber por que não dá pra só "aplicar
   * preto" ou "aplicar transparente".
   */
  function limparProp(prop: "color" | "background-color") {
    if (!edRef.current) return;
    try {
      document.execCommand("styleWithCSS", false, "true");
      document.execCommand(prop === "color" ? "foreColor" : "hiliteColor", false, SENTINELA);
    } catch {
      /* ignora */
    }
    // Limpa o que virou DOM agora; o que ficar pendente no navegador é pego
    // pela varredura da próxima emissão (ver varrerSentinelas).
    varrerSentinelas();
  }

  function desligar(prop: "color" | "background-color") {
    setOpen(false);
    edRef.current?.focus();
    limparProp(prop);
    marcarPendente(prop === "color" ? { cor: "" } : { grifo: false });
    emitir();
  }

  /** Clique numa cor: liga, ou DESLIGA se já for a cor em uso. */
  function alternarCor(hex: string) {
    if (fmt.cor === hex.toLowerCase()) {
      desligar("color");
    } else {
      comando("foreColor", hex);
      marcarPendente({ cor: hex.toLowerCase() });
      // Só entra no histórico quando a cor é APLICADA (desligar não conta —
      // senão tirar a cor jogaria ela de volta pro topo dos recentes).
      registrarCorUsada(hex);
    }
    setPaletaAberta(false);
  }

  function alternarGrifo() {
    if (fmt.grifo) {
      desligar("background-color");
    } else {
      comando("hiliteColor", COR_GRIFO);
      marcarPendente({ grifo: true });
    }
  }

  /**
   * "limpar" — tira TUDO: negrito, itálico, grifo e cor.
   *
   * Funciona com e sem seleção. Sem seleção, `removeFormat` do navegador não
   * faz nada (não há o que formatar), então desligamos também a intenção
   * pendente — que é o caso de quem clicou numa cor, viu que não era essa e
   * quer voltar ao normal antes de digitar. Era o "limpar não funciona"
   * relatado pelo Ricardo.
   */
  function limparTudo() {
    const ed = edRef.current;
    if (!ed) return;
    setOpen(false);
    setPaletaAberta(false);
    ed.focus();
    try {
      document.execCommand("styleWithCSS", false, "true");
      document.execCommand("removeFormat");
      if (document.queryCommandState("bold")) document.execCommand("bold");
      if (document.queryCommandState("italic")) document.execCommand("italic");
    } catch {
      /* ignora */
    }
    limparProp("color");
    limparProp("background-color");
    pendente.current = null;
    marcarPendente({ negrito: false, italico: false, grifo: false, cor: "" });
    emitir();
  }

  function inserirHtmlNoCursor(html: string) {
    setOpen(false);
    edRef.current?.focus();
    try {
      document.execCommand("insertHTML", false, html);
    } catch {
      /* ignora */
    }
    emitir();
  }

  // ---- @menção: no contenteditable a posição vem da Selection, não de selectionStart.
  function trechoDaMencao(): { node: Text; inicio: number; fim: number; termo: string } | null {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return null;
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) return null;
    if (!edRef.current?.contains(node)) return null;
    const fim = sel.anchorOffset;
    const antes = (node.textContent || "").slice(0, fim);
    const m = RE_MENCAO.exec(antes);
    if (!m) return null;
    return { node: node as Text, inicio: fim - m[0].length, fim, termo: m[1] };
  }

  function aoDigitar() {
    emitir();
    const t = trechoDaMencao();
    if (t) {
      setQuery(t.termo);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }

  function aplicarMencao(u: UsuarioAtivo) {
    const t = trechoDaMencao();
    setOpen(false);
    setQuery("");
    if (!t) return;
    const texto = t.node.textContent || "";
    const inserido = `@${handleFromNome(u.nome)} `;
    t.node.textContent = texto.slice(0, t.inicio) + inserido + texto.slice(t.fim);
    // Recoloca o cursor logo depois do nome inserido.
    const pos = t.inicio + inserido.length;
    const sel = window.getSelection();
    const range = document.createRange();
    range.setStart(t.node, Math.min(pos, (t.node.textContent || "").length));
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
    emitir();
  }

  const sugestoes = useMemo(() => {
    if (!open || query.length === 0) return usuarios.slice(0, 5);
    const q = query.toLowerCase();
    return usuarios
      .filter((u) => handleFromNome(u.nome).startsWith(q) || (u.email || "").toLowerCase().startsWith(q))
      .slice(0, 5);
  }, [open, query, usuarios]);

  useEffect(() => { setActiveIndex(0); }, [query, open]);

  function aoTeclar(e: React.KeyboardEvent<HTMLDivElement>) {
    // Enter "envia" (quando onEnter foi passado) — mas SÓ com o dropdown de
    // menção fechado, senão o Enter é do dropdown (escolher a pessoa). Shift+
    // Enter sempre faz nova linha.
    if (onEnter && e.key === "Enter" && !e.shiftKey && !(open && sugestoes.length > 0)) {
      e.preventDefault();
      emitir();
      onEnter();
      return;
    }
    // Enter dentro de um item de checklist continua a lista (item vazio encerra).
    if (e.key === "Enter" && !e.shiftKey && !(open && sugestoes.length > 0)) {
      if (enterNoChecklist()) {
        e.preventDefault();
        return;
      }
    }
    if (!open || sugestoes.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % sugestoes.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + sugestoes.length) % sugestoes.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      aplicarMencao(sugestoes[Math.min(activeIndex, sugestoes.length - 1)]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  /**
   * Colar. Imagem → upload e entra inline. Qualquer outra coisa → entra como
   * TEXTO PURO, de propósito: colar de Word/Drive traria fontes, tabelas e
   * cores de fora que a nossa marcação não sabe representar (e sumiriam no
   * salvar, o que pareceria perda de conteúdo).
   */
  async function aoColar(e: React.ClipboardEvent<HTMLDivElement>) {
    const item = Array.from(e.clipboardData?.items || []).find(
      (it) => it.kind === "file" && it.type.startsWith("image/"),
    );
    if (item && onUploadImagem) {
      const file = item.getAsFile();
      if (file) {
        e.preventDefault();
        e.stopPropagation();
        await subirEInserir([file]);
      }
      return;
    }
    e.preventDefault();
    const txt = e.clipboardData?.getData("text/plain") || "";
    try {
      document.execCommand("insertText", false, txt);
    } catch {
      /* ignora */
    }
    emitir();
  }

  /** Sobe as imagens e insere inline no cursor (usado por COLAR e por ARRASTAR). */
  async function subirEInserir(files: File[]) {
    const imagens = files.filter((f) => f.type.startsWith("image/"));
    if (imagens.length === 0 || !onUploadImagem) return;
    setEnviandoImg(true);
    try {
      for (const file of imagens) {
        const url = await onUploadImagem(file);
        inserirHtmlNoCursor(`<img src="${url}" alt="" />`);
      }
    } catch {
      /* falha no upload — a pessoa tenta de novo */
    } finally {
      setEnviandoImg(false);
    }
  }

  /**
   * ARRASTAR E SOLTAR imagem dentro da caixa (pedido da Rec, jul/2026) — mesmo
   * resultado do Ctrl+V: a imagem entra INLINE no texto, no ponto do cursor.
   * Só reage quando o arrasto traz arquivo(s) de imagem; arrastar texto segue o
   * comportamento nativo do contenteditable.
   */
  function arrastoTemImagem(dt: DataTransfer | null) {
    if (!dt) return false;
    if (dt.items && dt.items.length > 0) {
      return Array.from(dt.items).some((it) => it.kind === "file" && it.type.startsWith("image/"));
    }
    return Array.from(dt.types || []).includes("Files");
  }

  function aoArrastarSobre(e: React.DragEvent<HTMLDivElement>) {
    if (!onUploadImagem || !arrastoTemImagem(e.dataTransfer)) return;
    e.preventDefault();          // sem isso o navegador ABRE a imagem e sai da tela
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    if (!dragImg) setDragImg(true);
  }

  function aoSairArrasto(e: React.DragEvent<HTMLDivElement>) {
    // Só desliga quando o ponteiro sai de verdade do editor (não ao passar por um filho).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragImg(false);
  }

  async function aoSoltar(e: React.DragEvent<HTMLDivElement>) {
    if (!onUploadImagem || !arrastoTemImagem(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragImg(false);
    // Coloca o cursor onde a pessoa soltou, pra imagem entrar naquele ponto.
    posicionarCursorNoPonto(e.clientX, e.clientY);
    const files = Array.from(e.dataTransfer.files || []);
    await subirEInserir(files);
  }

  /** Move o cursor pro ponto (x,y) da tela — assim a imagem cai onde foi solta. */
  function posicionarCursorNoPonto(x: number, y: number) {
    const el = edRef.current;
    if (!el) return;
    el.focus();
    const doc = document as any;
    let range: Range | null = null;
    if (doc.caretRangeFromPoint) range = doc.caretRangeFromPoint(x, y);
    else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(x, y);
      if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset); range.collapse(true); }
    }
    if (range && el.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }

  // Um botão LIGADO fica com fundo/anel azul — é o "essa função está em uso"
  // pedido pelo Ricardo. Clicar de novo desliga (todos os botões alternam).
  const btn = (ativo: boolean) =>
    "w-6 h-6 rounded border text-xs transition-colors " +
    (ativo
      ? "border-blue-500 bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-500"
      : "border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700");

  /** Bolinha de cor: quando é a cor em uso, ganha anel escuro em volta. */
  const bolinha = (ativo: boolean) =>
    "w-5 h-5 rounded-full border transition-all " +
    (ativo
      ? "border-slate-800 dark:border-white ring-2 ring-offset-1 ring-slate-800 dark:ring-white ring-offset-white dark:ring-offset-slate-800 scale-110"
      : "border-slate-300 dark:border-slate-600 hover:scale-110");

  const corLivreAtiva = !!fmt.cor && !Object.values(COR_HEX).includes(fmt.cor);

  // CORES NA BARRA: só 3 (o Ricardo achou que 11 bolinhas poluíam). São as 3
  // ÚLTIMAS USADAS — a barra se molda a quem está usando —, completadas com
  // vermelho/verde/azul enquanto não há histórico suficiente. Todo o resto mora
  // no submenu do botão de cor.
  const coresRecentes = useCoresRecentes();
  const FIXAS = Object.values(COR_HEX).map((h) => h.toLowerCase());
  const CORES_NA_BARRA = 3;
  const coresBarra = Array.from(new Set([...coresRecentes, ...FIXAS])).slice(0, CORES_NA_BARRA);
  /** Tudo que o submenu oferece, sem repetir: as nomeadas + a paleta estendida. */
  const todasAsCores = Array.from(new Set([...FIXAS, ...PALETA_EXTRA.map((h) => h.toLowerCase())]));

  const swatchesBarra = (
    <>
      {coresBarra.map((hex) => (
        <button
          key={hex}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => alternarCor(hex)}
          title={fmt.cor === hex ? `Cor ${hex} (em uso — clique pra tirar)` : `Cor ${hex}`}
          aria-pressed={fmt.cor === hex}
          className={bolinha(fmt.cor === hex)}
          style={{ backgroundColor: hex }}
        />
      ))}
      {/* Abre o submenu com recentes + todas as cores + seletor livre. */}
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => abrirPaleta(e.currentTarget)}
        title="Mais cores"
        aria-pressed={corLivreAtiva}
        className={
          "h-6 px-1 rounded border text-[9px] flex items-center gap-0.5 transition-colors " +
          (corLivreAtiva
            ? "border-blue-500 bg-blue-100 dark:bg-blue-900/50"
            : "border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700")
        }
      >
        <span
          className="w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-500"
          style={{
            background: fmt.cor
              ? fmt.cor
              : "conic-gradient(#ef4444,#f59e0b,#eab308,#22c55e,#06b6d4,#3b82f6,#a855f7,#ec4899,#ef4444)",
          }}
        />
        <span className="text-slate-500 dark:text-slate-300 leading-none">▾</span>
      </button>
    </>
  );

  /**
   * Os botões de formatação INLINE (os que agem sobre o texto selecionado).
   * Mesmo JSX na barra de cima e na flutuante — se um dia mudar o comportamento
   * de um botão, muda nos dois de uma vez. `onMouseDown` com preventDefault em
   * todos: sem isso, clicar no botão tira o foco e a SELEÇÃO SOME antes do
   * clique chegar — a barra flutuante não formataria nada.
   */
  const botoesInline = (
    <>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => comando("bold")} title={fmt.negrito ? "Negrito (ligado — clique pra desligar)" : "Negrito"} aria-pressed={fmt.negrito} className={`${btn(fmt.negrito)} font-bold`}>B</button>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => comando("italic")} title={fmt.italico ? "Itálico (ligado — clique pra desligar)" : "Itálico"} aria-pressed={fmt.italico} className={`${btn(fmt.italico)} italic`}>I</button>
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={alternarGrifo} title={fmt.grifo ? "Grifar (ligado — clique pra desligar)" : "Grifar"} aria-pressed={fmt.grifo} className={btn(fmt.grifo)}><span className="bg-yellow-200 px-0.5 rounded text-slate-800">G</span></button>
      <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />
      {swatchesBarra}
    </>
  );

  return (
    <div className="relative">
      {/* BARRA FLUTUANTE — aparece colada na seleção, some quando não há texto
          selecionado. Portal + posição fixa: o editor mora dentro de painéis
          com rolagem, um `absolute` seria recortado por eles. */}
      {bolha && createPortal(
        <div
          data-barra-flutuante="1"
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: "fixed",
            zIndex: 70,
            left: Math.max(8, Math.min(bolha.left - LARGURA_BOLHA / 2, window.innerWidth - LARGURA_BOLHA - 8)),
            // Acima da seleção; se não couber (seleção no topo da tela), vai pra baixo.
            top: bolha.top - ALTURA_BOLHA - 8 >= 8 ? bolha.top - ALTURA_BOLHA - 8 : bolha.bottom + 8,
            width: LARGURA_BOLHA,
          }}
          className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl"
        >
          {botoesInline}
          <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFonte("grande")} title="Aumentar fonte" className={`${btn(false)} font-bold`}>A+</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFonte("pequeno")} title="Diminuir fonte" className={`${btn(false)} text-[9px]`}>A-</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={limparTudo} title="Tirar toda a formatação" className={`${btn(false)} w-auto px-1.5 text-slate-500 text-[10px]`}>limpar</button>
        </div>,
        document.body,
      )}

      {toolbar && (
        <div className="flex items-center gap-1 mb-1 flex-wrap">
          {botoesInline}

          <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />
          {/* Listas + numeração + tamanho de fonte (2ª leva jul/2026). */}
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => comandoLista("insertUnorderedList")} title="Lista com marcadores" className={btn(false)}>•</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => comandoLista("insertOrderedList")} title="Lista numerada" className={`${btn(false)} text-[10px] leading-none`}>1.</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={inserirChecklist} title="Checklist (caixa pra marcar)" className={btn(false)}>☑</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFonte("grande")} title="Aumentar fonte (selecione o texto)" className={`${btn(false)} font-bold`}>A+</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicarFonte("pequeno")} title="Diminuir fonte (selecione o texto)" className={`${btn(false)} text-[9px]`}>A-</button>
          <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 mx-0.5" />

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={limparTudo}
            title="Tirar toda a formatação"
            className={`${btn(false)} w-auto px-1.5 text-slate-500`}
          >
            limpar
          </button>
          {enviandoImg && <span className="text-[10px] text-blue-500 ml-1">enviando imagem…</span>}
        </div>
      )}

      {paletaAberta && createPortal(
        <div
          ref={paletaRef}
          onMouseDown={(e) => e.preventDefault()}
          style={{
            position: "fixed",
            zIndex: 80,   // acima da barra flutuante (70), que pode ter aberto ele
            width: LARGURA_PALETA,
            left: paletaAncora
              ? Math.max(8, Math.min(paletaAncora.left, window.innerWidth - LARGURA_PALETA - 8))
              : 8,
            // Abre pra baixo; sobe quando não cabe (barra no rodapé da tela).
            top: paletaAncora
              ? (paletaAncora.bottom + ALTURA_PALETA + 8 <= window.innerHeight
                  ? paletaAncora.bottom + 6
                  : Math.max(8, paletaAncora.top - ALTURA_PALETA - 6))
              : 8,
          }}
          className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Escolher cor</span>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setPaletaAberta(false)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
          </div>
          {/* "Padrão" ANTES das cores: é o caminho pra voltar ao texto normal.
              Sem ele a pessoa clicava no preto achando que era isso — e no modo
              escuro o texto sumia no fundo. */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { desligar("color"); setPaletaAberta(false); }}
            className={
              "w-full mb-2 flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[11px] transition-colors " +
              (fmt.cor
                ? "border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
                : "border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200")
            }
          >
            <span className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-900 flex items-center justify-center text-[9px] font-bold text-slate-700 dark:text-slate-200">
              A
            </span>
            Cor padrão do texto
            {!fmt.cor && <span className="ml-auto text-[10px]">em uso</span>}
          </button>

          {/* RECENTES dentro da paleta: é aqui que a cor "de fora" foi escolhida
              da primeira vez, então é aqui que ela precisa estar à mão depois.
              Mostra todas as recentes (inclusive as fixas) — na paleta não há a
              redundância de ter a mesma bolinha ao lado. */}
          {coresRecentes.length > 0 && (
            <div className="mb-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Recentes</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {coresRecentes.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => alternarCor(hex)}
                    title={hex}
                    className={"w-5 h-5 rounded-full border " + (fmt.cor === hex ? "border-slate-800 dark:border-white ring-2 ring-slate-800 dark:ring-white" : "border-slate-300 dark:border-slate-600 hover:scale-110 transition-transform")}
                    style={{ backgroundColor: hex }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* TODAS — inclui as nomeadas (vermelho/verde/azul/laranja/roxo), que
              antes viviam só na barra. Agora que a barra mostra 3, elas
              PRECISAM estar aqui, senão sumiriam quando saíssem do histórico. */}
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Todas as cores</span>
          <div className="grid grid-cols-8 gap-1 mt-1">
            {todasAsCores.map((hex) => (
              <button
                key={hex}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => alternarCor(hex)}
                title={hex}
                className={"w-5 h-5 rounded-full border " + (fmt.cor === hex ? "border-slate-800 dark:border-white ring-2 ring-slate-800 dark:ring-white" : "border-slate-300 dark:border-slate-600 hover:scale-110 transition-transform")}
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer">
            <input
              type="color"
              defaultValue={RE_HEX.test(fmt.cor) ? fmt.cor : "#2563eb"}
              onChange={(e) => alternarCor(e.target.value)}
              className="w-6 h-6 p-0 border border-slate-300 dark:border-slate-600 rounded cursor-pointer bg-transparent"
            />
            qualquer outra cor
          </label>
        </div>,
        document.body,
      )}

      <div className="relative">
        <div
          ref={edRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onInput={aoDigitar}
          onKeyDown={aoTeclar}
          onPaste={aoColar}
          // Arrastar e soltar imagem — mesmo resultado do Ctrl+V (entra inline).
          onDragOver={aoArrastarSobre}
          onDragEnter={aoArrastarSobre}
          onDragLeave={aoSairArrasto}
          onDrop={aoSoltar}
          onBlur={emitir}
          // Clique/seta dentro do texto também atualiza a barra (o
          // selectionchange cobre a maioria dos casos, estes garantem o resto).
          // onClick também trata o toggle da caixa de checklist (.rt-box).
          onClick={aoClicar}
          onKeyUp={lerFormato}
          // `min-h` no lugar de `rows`: contenteditable não tem rows. ~22px por linha.
          style={{ minHeight: `${Math.max(1, rows) * 22 + 16}px` }}
          className={
            (className ||
              "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm") +
            // `editor-rico` é o gancho da regra de grifo no index.css — ver lá.
            " editor-rico overflow-y-auto whitespace-pre-wrap break-words [&_img]:max-w-full [&_img]:rounded [&_img]:my-1" +
            // Destaque quando há imagem sendo arrastada por cima.
            (dragImg ? " ring-2 ring-blue-400 border-blue-500 bg-blue-50/60 dark:bg-blue-900/20" : "")
          }
        />
        {dragImg && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[11px] font-medium text-blue-700 bg-blue-100/95 dark:bg-blue-900/80 dark:text-blue-200 px-2 py-1 rounded shadow-sm">
              Solte a imagem para inserir no texto
            </span>
          </div>
        )}
        {vazio && placeholder && !dragImg && (
          // Placeholder manual: contenteditable não tem o atributo nativo.
          <div className="absolute left-3 top-2 text-sm text-slate-400 pointer-events-none select-none">
            {placeholder}
          </div>
        )}
      </div>

      {open && sugestoes.length > 0 && (
        <div className="absolute z-50 mt-1 w-72 max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
          {sugestoes.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => aplicarMencao(u)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === activeIndex ? "bg-blue-50 dark:bg-slate-700" : "hover:bg-slate-50 dark:hover:bg-slate-700/60"}`}
            >
              <span className="font-medium text-slate-700 dark:text-slate-200">{u.nome}</span>
              <span className="text-[11px] text-slate-400">@{handleFromNome(u.nome)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
